/**
 * FeedbackWidget Component
 * Displays thumbs up/down feedback buttons after each answer
 * 
 * Sprint 1, Task 11: User Feedback Collection UI
 */

import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react';
import { useFeedbackStore } from '../../stores/feedbackStore';

export interface FeedbackWidgetProps {
  queryId: string;
  projectId: string;
  answer: string;
  citations?: any[];
  compact?: boolean;
  onFeedbackSubmit?: (feedback: any) => void;
  /** Called when user clicks "Provide Details" so parent can open its own modal */
  onProvideDetails?: () => void;
}

export const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  queryId,
  projectId,
  answer,
  citations = [],
  compact = false,
  onFeedbackSubmit,
  onProvideDetails,
}) => {
  const [feedbackGiven, setFeedbackGiven] = useState<'helpful' | 'not_helpful' | null>(null);
  const [showDetailedFeedback, setShowDetailedFeedback] = useState(false);
  const { submitFeedback, isSubmitting } = useFeedbackStore();

  const handleQuickFeedback = async (type: 'helpful' | 'not_helpful') => {
    if (feedbackGiven || isSubmitting) return;

    setFeedbackGiven(type);

    const feedback = {
      query_id: queryId,
      project_id: projectId,
      feedback_type: type,
      rating: type === 'helpful' ? 5 : 2,
      timestamp: new Date().toISOString()
    };

    try {
      await submitFeedback(feedback);
      onFeedbackSubmit?.(feedback);

      // Show detailed feedback form for negative feedback
      if (type === 'not_helpful') {
        setTimeout(() => setShowDetailedFeedback(true), 500);
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setFeedbackGiven(null);
    }
  };

  const handleDetailedFeedback = () => {
    setShowDetailedFeedback(true);
    onProvideDetails?.();
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-600">Was this helpful?</span>
        <button
          onClick={() => handleQuickFeedback('helpful')}
          disabled={!!feedbackGiven || isSubmitting}
          className={`p-1 rounded transition-colors ${
            feedbackGiven === 'helpful'
              ? 'text-green-600 bg-green-50'
              : 'text-gray-400 hover:text-green-600 hover:bg-gray-50'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title="Helpful"
        >
          <ThumbsUp className="w-4 h-4" />
        </button>
        <button
          onClick={() => handleQuickFeedback('not_helpful')}
          disabled={!!feedbackGiven || isSubmitting}
          className={`p-1 rounded transition-colors ${
            feedbackGiven === 'not_helpful'
              ? 'text-red-600 bg-red-50'
              : 'text-gray-400 hover:text-red-600 hover:bg-gray-50'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title="Not helpful"
        >
          <ThumbsDown className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      {!feedbackGiven ? (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Was this answer helpful?</span>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleQuickFeedback('helpful')}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ThumbsUp className="w-4 h-4" />
              Yes
            </button>
            
            <button
              onClick={() => handleQuickFeedback('not_helpful')}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ThumbsDown className="w-4 h-4" />
              No
            </button>

            <button
              onClick={handleDetailedFeedback}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageSquare className="w-4 h-4" />
              Provide Details
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {feedbackGiven === 'helpful' ? (
              <>
                <ThumbsUp className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700 font-medium">
                  Thanks for your feedback!
                </span>
              </>
            ) : (
              <>
                <ThumbsDown className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-700 font-medium">
                  Thanks for letting us know.
                </span>
              </>
            )}
          </div>

          {feedbackGiven === 'not_helpful' && !showDetailedFeedback && (
            <button
              onClick={handleDetailedFeedback}
              className="text-sm text-blue-600 hover:text-blue-700 underline"
            >
              Tell us more
            </button>
          )}
        </div>
      )}

      {isSubmitting && (
        <div className="mt-2 text-sm text-gray-500 animate-pulse">
          Submitting feedback...
        </div>
      )}
    </div>
  );
};

export default FeedbackWidget;

